import {
  AutoExportDataset,
  AutoExportFrequency,
  ExportFormat,
  type AutoExportSchedule,
  type CreateAutoExportScheduleBody,
} from "@/types/analytics-auto-export";

export type AnalyticsReportTab =
  | "overview"
  | "revenue"
  | "services"
  | "offers"
  | "operations"
  | "teams"
  | "reviews";

export const DEFAULT_SEND_HOUR_UTC = 6;

const HOTEL_DATASET_BY_TAB: Record<AnalyticsReportTab, AutoExportDataset> = {
  overview: AutoExportDataset.REVENUE_TOTAL,
  revenue: AutoExportDataset.REVENUE_TOTAL,
  services: AutoExportDataset.SERVICES_USAGE,
  offers: AutoExportDataset.SERVICES_USAGE,
  operations: AutoExportDataset.OPERATIONS_TOTAL_REQUESTS,
  reviews: AutoExportDataset.REVIEWS_RATINGS,
  teams: AutoExportDataset.TEAMS_ACTIVITY,
};

const TENANT_DATASET_BY_TAB: Record<AnalyticsReportTab, AutoExportDataset> = {
  overview: AutoExportDataset.TENANT_REVENUE,
  revenue: AutoExportDataset.TENANT_REVENUE,
  services: AutoExportDataset.TENANT_SERVICES,
  offers: AutoExportDataset.TENANT_OFFERS,
  operations: AutoExportDataset.TENANT_OPERATIONS,
  reviews: AutoExportDataset.TENANT_REVIEWS,
  teams: AutoExportDataset.TENANT_TEAMS,
};

const TENANT_ONLY_TABS = new Set<AnalyticsReportTab>(["offers"]);

export function normalizeAnalyticsTab(tab: string): AnalyticsReportTab {
  const normalized = tab === "team" ? "teams" : tab;
  if (normalized in HOTEL_DATASET_BY_TAB) {
    return normalized as AnalyticsReportTab;
  }
  return "overview";
}

export function isReportTabAvailable(
  tab: AnalyticsReportTab,
  isTenantAdmin: boolean,
): boolean {
  if (isTenantAdmin) {
    return true;
  }
  return !TENANT_ONLY_TABS.has(tab);
}

export function mapTabToDataset(
  tab: AnalyticsReportTab,
  isTenantAdmin: boolean,
): AutoExportDataset {
  const map = isTenantAdmin ? TENANT_DATASET_BY_TAB : HOTEL_DATASET_BY_TAB;
  return map[tab];
}

export function mapDatasetToTab(
  dataset: AutoExportDataset,
  isTenantAdmin: boolean,
): AnalyticsReportTab {
  const map = isTenantAdmin ? TENANT_DATASET_BY_TAB : HOTEL_DATASET_BY_TAB;
  const entry = Object.entries(map).find(([, value]) => value === dataset);
  return (entry?.[0] as AnalyticsReportTab | undefined) ?? "overview";
}

export function mapPeriodToFrequency(period: string): AutoExportFrequency {
  switch (period) {
    case "daily":
      return AutoExportFrequency.DAILY;
    case "weekly":
      return AutoExportFrequency.WEEKLY;
    case "monthly":
    default:
      return AutoExportFrequency.MONTHLY;
  }
}

export function mapFrequencyToPeriod(
  frequency: AutoExportFrequency,
): "daily" | "weekly" | "monthly" {
  switch (frequency) {
    case AutoExportFrequency.DAILY:
      return "daily";
    case AutoExportFrequency.WEEKLY:
      return "weekly";
    case AutoExportFrequency.MONTHLY:
    default:
      return "monthly";
  }
}

export function buildCreateScheduleBody(params: {
  reportTab: AnalyticsReportTab;
  isTenantAdmin: boolean;
  hotelId?: string | null;
  title: string;
  description?: string;
  recipients: string[];
  period: string;
  format?: ExportFormat;
}): CreateAutoExportScheduleBody {
  const dataset = mapTabToDataset(params.reportTab, params.isTenantAdmin);

  return {
    dataset,
    hotelId: params.isTenantAdmin ? undefined : (params.hotelId ?? undefined),
    title: params.title.trim(),
    description: params.description?.trim() || undefined,
    format: params.format ?? ExportFormat.CSV,
    recipients: params.recipients,
    frequency: mapPeriodToFrequency(params.period),
    sendHourUtc: DEFAULT_SEND_HOUR_UTC,
  };
}

export function scheduleToFormState(
  schedule: AutoExportSchedule,
  isTenantAdmin: boolean,
): {
  reportTab: AnalyticsReportTab;
  title: string;
  description: string;
  period: "daily" | "weekly" | "monthly";
  extraEmails: string[];
} {
  return {
    reportTab: mapDatasetToTab(schedule.dataset, isTenantAdmin),
    title: schedule.title,
    description: schedule.description ?? "",
    period: mapFrequencyToPeriod(schedule.frequency),
    extraEmails: [...schedule.recipients],
  };
}
